import { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../../../components/base/Modal";
import ImagePreviewModal from "../../../../../../components/base/ImagePreviewModal";
import type { Aviso } from "../../../../../../store/admin/avisosStore";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import type { ThemeClasses } from "../types";

interface DetailsAvisoModalProps {
  aviso: Aviso;
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  companyName?: string;
  onClose: () => void;
  onEdit?: (aviso: Aviso) => void;
}

const formatTimestamp = (
  ts?: { _seconds: number; _nanoseconds: number } | null,
  locale = "es",
): string | null => {
  if (!ts || typeof ts._seconds !== "number") return null;
  const date = new Date(ts._seconds * 1000);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(locale, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function DetailsAvisoModal({
  aviso,
  isDarkMode,
  themeClasses,
  companyName,
  onClose,
  onEdit,
}: DetailsAvisoModalProps) {
  const { t, i18n } = useTranslation("adminSections");
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const disableCRUD = useDisableCRUD();

  const fallbackTitle = t("avisos.title");
  const readedCount = Array.isArray(aviso.readed_by)
    ? aviso.readed_by.length
    : 0;
  const scopeCompanyName = companyName || aviso.company_id || "";

  const createdAt = formatTimestamp(aviso.createdAt, i18n.language);
  const updatedAt = formatTimestamp(aviso.updatedAt, i18n.language);

  return (
    <>
      <Modal
        themeClasses={themeClasses}
        onClose={onClose}
        title={t("avisos.detailsTitle")}
        showEditButton={!!onEdit}
        onEdit={onEdit ? () => onEdit(aviso) : undefined}
        disableCRUD={disableCRUD}
        content={
          <div className="space-y-4">
            {aviso.image ? (
              <button
                type="button"
                onClick={() => setImagePreviewOpen(true)}
                className="relative group w-full rounded-lg overflow-hidden focus:outline-none block"
                aria-label={t("avisos.form.enlargeImage")}
              >
                <img
                  src={aviso.image}
                  alt={aviso.title || fallbackTitle}
                  className="w-full max-h-72 object-contain bg-black/5 dark:bg-black/30"
                />
                <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
                  <i className="ri-zoom-in-line text-white text-3xl opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </span>
              </button>
            ) : (
              <div
                className={`w-full h-40 flex items-center justify-center rounded-lg ${
                  isDarkMode ? "bg-gray-800" : "bg-gray-100"
                }`}
              >
                <i
                  className={`ri-megaphone-line text-5xl ${themeClasses.textSecondary}`}
                ></i>
              </div>
            )}

            <div className="flex items-start justify-between gap-3 flex-wrap">
              <h3
                className={`font-semibold text-lg ${themeClasses.text} break-words`}
              >
                {aviso.title || t("avisos.noTitle")}
              </h3>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                  aviso.status === "active"
                    ? "text-primary-500 bg-primary-500/10"
                    : "text-gray-500 bg-gray-500/10"
                }`}
              >
                {aviso.status === "active"
                  ? t("avisos.active")
                  : t("avisos.inactive")}
              </span>
            </div>

            <div className="flex items-center gap-2 text-xs flex-wrap">
              {aviso.company_id ? (
                <span
                  className="px-2 py-0.5 rounded-full font-medium text-blue-500 bg-blue-500/10"
                  title={t("avisos.scopeCompanyLabel", {
                    name: scopeCompanyName,
                  })}
                >
                  <i className="ri-building-line mr-1"></i>
                  {companyName || aviso.company_id}
                </span>
              ) : (
                <span
                  className="px-2 py-0.5 rounded-full font-medium text-purple-500 bg-purple-500/10"
                  title={t("avisos.scopeGlobalLabel")}
                >
                  <i className="ri-global-line mr-1"></i>
                  {t("avisos.allCompanies")}
                </span>
              )}
              <span
                className={`px-2 py-0.5 rounded-full ${themeClasses.textSecondary}`}
                title={t("avisos.readedByTitle")}
              >
                <i className="ri-eye-line mr-1"></i>
                {t("avisos.readedByCount", { count: readedCount })}
              </span>
            </div>

            <div>
              <p
                className={`text-xs uppercase tracking-wide mb-1 ${themeClasses.textSecondary}`}
              >
                {t("avisos.form.descriptionLabel")}
              </p>
              <p
                className={`text-sm whitespace-pre-wrap ${themeClasses.text}`}
              >
                {aviso.description || t("avisos.noDescription")}
              </p>
            </div>

            {(createdAt || updatedAt) && (
              <div
                className={`grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs border-t pt-3 ${themeClasses.cardBorder}`}
              >
                {createdAt && (
                  <div>
                    <p className={`${themeClasses.textSecondary} mb-0.5`}>
                      {t("avisos.createdAt")}
                    </p>
                    <p className={themeClasses.text}>{createdAt}</p>
                  </div>
                )}
                {updatedAt && (
                  <div>
                    <p className={`${themeClasses.textSecondary} mb-0.5`}>
                      {t("avisos.updatedAt")}
                    </p>
                    <p className={themeClasses.text}>{updatedAt}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        }
      />

      {imagePreviewOpen && aviso.image && (
        <ImagePreviewModal
          src={aviso.image}
          alt={aviso.title || fallbackTitle}
          onClose={() => setImagePreviewOpen(false)}
        />
      )}
    </>
  );
}
